import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCartStore } from "../store/cart.store";
import { useAuth } from "../context/AuthProvider";
import { placeOrder, quoteOrder, ServerQuote } from "../services/order.service";
import { getUserProfile } from "../services/user.service";
import { toast } from "react-hot-toast";
import { Card } from "../components/common/Card";
import { Button } from "../components/common/Button";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { parseAndValidatePrice } from "../utils/priceValidator";
import { UPIQRModal } from "../components/payment/UPIQRModal";
import { AddMoneyModal } from "../components/wallet/AddMoneyModal";
import { useCommunityOrder } from "../hooks/useCommunityOrder";
import { AlertTriangle, Wallet, CreditCard, Clock } from "lucide-react";
import { useLiveWallet } from "../hooks/useLiveWallet";
import { newIdempotencyKey } from "../services/callables";

type PaymentMethod = "wallet" | "upi" | "upi-qr" | "food";

const CHECKOUT_KEY_LS = "ruchira_checkout_key";
const AUTOPLACE_LS = (txnid: string) => `ruchira_payu_autoplace_${txnid}`;
const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const ceil2 = (n: number) => Math.ceil(round2(n) * 100 - 1e-6) / 100;

/** One idempotency key per checkout attempt; survives the PayU round-trip and reloads. */
const getCheckoutKey = (): string => {
  try {
    const existing = localStorage.getItem(CHECKOUT_KEY_LS);
    if (existing && existing.length >= 8) return existing;
    const k = newIdempotencyKey("co");
    localStorage.setItem(CHECKOUT_KEY_LS, k);
    return k;
  } catch {
    return newIdempotencyKey("co");
  }
};
const clearCheckoutKey = () => {
  try { localStorage.removeItem(CHECKOUT_KEY_LS); } catch { /* ignore */ }
};

/**
 * Multi-kitchen checkout (1 parent_orders doc + 1 orders doc per kitchen — created server-side).
 *
 * PayU path: PayU charges only the shortfall beyond the live wallet balance. The server credits
 * wallets/{uid} (surl / webhook / reconciler), redirects to /checkout?payu=success&txnid=…, and we
 * auto-place the order from the wallet once profile + fees + the credited intent are loaded,
 * using the *current* cart/fee values and the checkout idempotency key (so reloads can't
 * double-place).
 */
const Checkout: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { items, totalPrice, clearCart, kitchenCount, removeItem } = useCartStore();
  const numKitchens = kitchenCount();

  const [loading, setLoading] = useState(false);
  const [selectedFallbackMethod, setSelectedFallbackMethod] =
    useState<PaymentMethod>("food");

  const [showUPIModal, setShowUPIModal] = useState(false);
  const [showAddMoney, setShowAddMoney] = useState(false);
  const [addMoneyPurpose, setAddMoneyPurpose] = useState<
    "wallet_recharge" | "order_payment"
  >("order_payment");
  const [addMoneyAmount, setAddMoneyAmount] = useState<number | undefined>();
  const [orderId] = useState(`ORD${Date.now()}`);

  const [merchantVpa, setMerchantVpa] = useState("ruchira@upi");
  const [merchantName, setMerchantName] = useState("Ruchira Home Food");
  const [profile, setProfile] = useState<any | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);

  // PayU return captured once from the URL (URL is cleaned immediately)
  const [payuReturn, setPayuReturn] = useState<{ status: string; txnid: string } | null>(null);
  const [autoPlaceMsg, setAutoPlaceMsg] = useState<string | null>(null);
  const autoPlaceLock = useRef(false);

  // ---- Server quote: every price / fee comes from quoteOrder (live menu + config/rates) ----
  const [quote, setQuote] = useState<ServerQuote | null>(null);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteNonce, setQuoteNonce] = useState(0);
  const cartSig = JSON.stringify(items.map((i) => [i.id, i.quantity, i.kitchenId, i.menuItemId || null]));
  const community = useCommunityOrder();

  const clientSubtotal = totalPrice();
  const subtotal = quote ? quote.subtotal : clientSubtotal;
  const deliveryFee = quote ? quote.deliveryFee : 0;
  const platformFee = quote ? quote.platformFee : 0;
  const totalWithDelivery = quote ? quote.totalAmount : round2(clientSubtotal);
  const problems = quote?.problems || [];
  const cutoffKitchens = quote?.cutoffKitchens || [];

  // Single balance source: wallets/{uid}.balance (live)
  const { balance: walletBalance, loaded: walletLoaded } = useLiveWallet(user?.uid, profile?.walletBalance);
  const isWalletSufficient = walletLoaded && walletBalance >= totalWithDelivery;
  const deficit = Math.max(0, round2(totalWithDelivery - walletBalance));
  const shortfall = deficit > 0 ? Math.max(1, ceil2(deficit)) : 0;
  const feeLoading = !quote && !quoteError;
  const feesReady = !!quote && problems.length === 0;
  const notReadyMsg = () =>
    problems.length ? "Remove the unavailable items first." : quoteError ? quoteError : "Calculating your bill...";

  const latest = useRef({ items, subtotal, deliveryFee, platformFee, totalWithDelivery, profile });
  latest.current = { items, subtotal, deliveryFee, platformFee, totalWithDelivery, profile };

  const refreshProfile = useCallback(async () => {
    if (!user?.uid) return null;
    try {
      const p = await getUserProfile(user.uid);
      setProfile(p);
      return p;
    } finally {
      setProfileLoaded(true);
    }
  }, [user?.uid]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  useEffect(() => {
    if (!user?.uid || !profileLoaded || items.length === 0) return;
    let alive = true;
    setQuote(null);
    setQuoteError(null);
    quoteOrder(latest.current.items, latest.current.profile?.address || null)
      .then((q) => { if (alive) setQuote(q); })
      .catch((e) => { if (alive) setQuoteError(e?.message || "Couldn't price your cart."); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid, profileLoaded, cartSig, profile?.address, quoteNonce]);

  const submitOrder = useCallback(
    async (method: "wallet" | "upi-qr" | "food", opts: { payuTxnid?: string; idempotencyKey?: string } = {}) => {
      if (!user) throw new Error("Please login");
      const cur = latest.current;
      if (cur.items.length === 0) throw new Error("Cart is empty");
      const res = await placeOrder(
        user.uid,
        cur.items,
        cur.subtotal,
        method,
        method === "upi-qr" ? "pending_verification" : method === "wallet" ? "paid" : "food",
        cur.deliveryFee,
        {
          platformFee: cur.platformFee,
          customerName: cur.profile?.name || user.displayName || "Customer",
          customerPhone: cur.profile?.phone || "",
          deliveryAddress: cur.profile?.address || null,
          idempotencyKey: opts.idempotencyKey || getCheckoutKey(),
          payuTxnid: opts.payuTxnid,
          expectedTotal: cur.totalWithDelivery,
        }
      );
      clearCheckoutKey();
      clearCart();
      if (res.awaitingChef && res.awaitingChef.length) {
        const names = res.awaitingChef.map((a) => a.kitchenName).join(", ");
        toast(`Past cut-off for ${names}: we've asked the chef. You'll hear back within ${res.confirmMinutes || 15} minutes; if they can't make it, that part is cancelled${method === "wallet" ? " and refunded to your wallet" : ""}.`, { icon: "⏳", duration: 10000 });
      }
      return res;
    },
    [user, clearCart]
  );

  const handlePlaceOrder = useCallback(
    async (userConfirmedPayment: boolean = false) => {
      if (!user) return toast.error("Please login");
      if (items.length === 0) return toast.error("Cart is empty");
      if (!feesReady) return toast.error(notReadyMsg());

      let method: "wallet" | "upi-qr" | "food";
      if (isWalletSufficient) method = "wallet";
      else if (selectedFallbackMethod === "upi" && userConfirmedPayment) method = "upi-qr";
      else method = "food";

      setLoading(true);
      try {
        await submitOrder(method);
        setShowUPIModal(false);
        toast.success("Order placed successfully!");
        navigate("/orders");
      } catch (error: any) {
        console.error(error);
        toast.error(error?.message || "Order failed. Please try again.");
        if (/PRICE_CHANGED|no longer|unavailable|closed/i.test(String(error?.message))) setQuoteNonce((n) => n + 1);
      } finally {
        setLoading(false);
      }
    },
    [user, items.length, feesReady, isWalletSufficient, selectedFallbackMethod, submitOrder, navigate]
  );

  // 1) Capture ?payu=…&txnid=… once and clean the URL (no re-trigger on reload)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const payu = params.get("payu");
    if (!payu) return;
    const txnid = (params.get("txnid") || "").replace(/[^A-Za-z0-9_-]/g, "");
    setPayuReturn({ status: payu, txnid });
    navigate(location.pathname, { replace: true });
    if (payu === "pending") {
      toast("Payment is pending with the bank. Your wallet will be credited automatically once PayU confirms (usually within 15 minutes); you can then place the order.", { icon: "⏳", duration: 9000 });
    } else if (payu === "failed") {
      toast.error("Payment failed or was cancelled. No money was added.");
    } else if (payu !== "success") {
      toast.error(`We couldn't verify this payment${txnid ? ` (ref ${txnid})` : ""}. If money was debited it will be reconciled automatically.`, { duration: 9000 });
    }
  }, [location.search, location.pathname, navigate]);

  // 2) Auto-place after a successful PayU top-up — waits for user, profile, fees and wallet.
  useEffect(() => {
    if (!payuReturn || payuReturn.status !== "success" || !payuReturn.txnid) return;
    if (!user?.uid || !profileLoaded || !feesReady || !walletLoaded) return;
    if (autoPlaceLock.current) return;
    autoPlaceLock.current = true;

    const txnid = payuReturn.txnid;
    const flagKey = AUTOPLACE_LS(txnid);
    (async () => {
      try {
        let flag: string | null = null;
        try { flag = localStorage.getItem(flagKey); } catch { /* ignore */ }
        if (flag === "done") {
          setAutoPlaceMsg(null);
          toast.success("Order for this payment was already placed.");
          navigate("/orders");
          return;
        }

        // Wait for the server to mark the intent credited (surl normally does it before redirect)
        setAutoPlaceMsg("Confirming your payment…");
        let intent: any = null;
        for (let i = 0; i < 15; i++) {
          const snap = await getDoc(doc(db, "payu_intents", txnid));
          intent = snap.exists() ? snap.data() : null;
          if (intent && ["credited", "failed", "mismatch", "expired"].includes(intent.status)) break;
          await new Promise((r) => setTimeout(r, 1500));
        }
        if (!intent || intent.status !== "credited") {
          setAutoPlaceMsg(null);
          toast(intent?.status === "pending" || intent?.status === "created" || !intent
            ? "Payment is still being confirmed. Your wallet will be credited automatically; place the order once the balance shows."
            : `Payment was not credited (${intent?.status}).`, { icon: "ℹ️", duration: 9000 });
          autoPlaceLock.current = false;
          return;
        }

        const cur = latest.current;
        if (cur.items.length === 0) {
          setAutoPlaceMsg(null);
          toast.success(`₹${Number(intent.amount || 0).toFixed(2)} added to your wallet.`);
          return;
        }
        // Fresh balance (don't trust a stale render)
        const wSnap = await getDoc(doc(db, "wallets", user.uid));
        const bal = wSnap.exists() ? Number(wSnap.data()?.balance || 0) : Number(cur.profile?.walletBalance || 0);
        if (bal + 0.001 < cur.totalWithDelivery) {
          setAutoPlaceMsg(null);
          toast.error(`Wallet ₹${bal.toFixed(2)} is short of ₹${cur.totalWithDelivery.toFixed(2)} (cart or fees changed). Pay the remaining amount to continue.`, { duration: 9000 });
          autoPlaceLock.current = false;
          return;
        }

        setAutoPlaceMsg("Payment received. Placing your order…");
        try { localStorage.setItem(flagKey, "running"); } catch { /* ignore */ }
        await submitOrder("wallet", {
          payuTxnid: txnid,
          idempotencyKey: intent.checkoutKey || getCheckoutKey(),
        });
        try { localStorage.setItem(flagKey, "done"); } catch { /* ignore */ }
        toast.success("Order placed successfully!");
        navigate("/orders");
      } catch (e: any) {
        console.error(e);
        setAutoPlaceMsg(null);
        toast.error(e?.message || "Couldn't place the order. Your wallet was credited — tap Pay to retry.");
        autoPlaceLock.current = false;
      }
    })();
  }, [payuReturn, user?.uid, profileLoaded, feesReady, walletLoaded, submitOrder, navigate]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settingsRef = doc(db, "settings", "global");
        const snap = await getDoc(settingsRef);
        if (snap.exists()) {
          const data = snap.data();
          if (data.upiId) setMerchantVpa(data.upiId);
          if (data.merchantName) setMerchantName(data.merchantName);
        }
      } catch (error) {
        console.error("Error fetching payment settings:", error);
      }
    };
    fetchSettings();
  }, []);

  // PayU charges only the shortfall; the wallet then pays for the whole order.
  const openPayUForOrder = () => {
    if (!feesReady) return toast.error(notReadyMsg());
    getCheckoutKey();
    setAddMoneyPurpose("order_payment");
    setAddMoneyAmount(shortfall);
    setShowAddMoney(true);
  };

  const handleCheckoutClick = () => {
    if (!feesReady) return toast.error(notReadyMsg());
    if (isWalletSufficient) {
      handlePlaceOrder();
      return;
    }
    if (selectedFallbackMethod === "upi") {
      setShowUPIModal(true);
    } else {
      handlePlaceOrder();
    }
  };

  if (autoPlaceMsg) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <div className="animate-spin h-10 w-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-4" />
          <p className="font-semibold text-gray-800">{autoPlaceMsg}</p>
          <p className="text-xs text-gray-500 mt-2">Please don&apos;t close this page.</p>
        </Card>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center space-y-3">
          <h2 className="text-xl font-bold text-gray-900">Your cart is empty</h2>
          {walletLoaded && (
            <p className="text-sm text-gray-600">Wallet balance: <strong>₹{walletBalance.toFixed(2)}</strong></p>
          )}
          <div className="flex gap-2 justify-center">
            <Button onClick={() => navigate("/")}>Browse kitchens</Button>
            <Button variant="outline" onClick={() => navigate("/orders")}>My orders</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 pb-32">
      <div className="max-w-2xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Checkout</h1>

        <Card className="p-6">
          <h3 className="font-bold text-gray-800 mb-4 border-b pb-2 flex items-center justify-between">
            <span>Order Summary</span>
            {numKitchens > 1 && (
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">
                {numKitchens} kitchens
              </span>
            )}
          </h3>
          <div className="space-y-3">
            {items.map((item, idx) => {
              const priced = quote && problems.length === 0 ? quote.items[idx] : undefined;
              const itemPrice = priced ? priced.price : parseAndValidatePrice(item.price);
              const problem = problems.find((p) => p.index === idx);
              return (
                <div key={item.id} className="flex justify-between items-start text-sm">
                  <div>
                    <span className="font-bold">{item.quantity}x</span> {item.name}
                    {numKitchens > 1 && item.kitchenName && (
                      <span className="block text-xs text-gray-400">{item.kitchenName}</span>
                    )}
                    {priced?.pastCutoff && (
                      <span className="block text-xs text-amber-700">Past {priced.cutoff} cut-off: we&apos;ll ask the chef</span>
                    )}
                    {problem && (
                      <span className="block text-xs text-red-600">
                        {problem.reason}{" "}
                        <button type="button" className="underline font-semibold" onClick={() => removeItem(item.id)}>Remove</button>
                      </span>
                    )}
                  </div>
                  <div className="font-mono">₹{(itemPrice * item.quantity).toFixed(2)}</div>
                </div>
              );
            })}

            <div className="border-t pt-3 mt-3">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Subtotal</span>
                <span>₹{subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2 p-2 bg-blue-50 rounded">
                <span className="text-gray-700">Delivery Fee</span>
                <span className="font-semibold">₹{deliveryFee.toFixed(2)}</span>
              </div>
              {(quote?.isCommunity ?? community.isCommunity) && (
                <p className="text-xs text-green-700 mb-2">
                  Community delivery · {community.communityName || "Bren Northern Lights"}
                </p>
              )}
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Platform fee</span>
                <span className="font-semibold">₹{platformFee.toFixed(2)}</span>
              </div>
            </div>

            <div className="border-t pt-3 flex justify-between items-center text-lg font-bold mt-4">
              <span>Total Payable</span>
              <span className="text-orange-600">₹{totalWithDelivery.toFixed(2)}</span>
            </div>
          </div>
        </Card>

        {quoteError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center justify-between gap-3">
            <span>{quoteError}</span>
            <Button size="sm" variant="outline" onClick={() => setQuoteNonce((n) => n + 1)}>Retry</Button>
          </div>
        )}
        {problems.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
            Some items can&apos;t be ordered right now: {problems.map((p) => `${p.name} (${p.reason})`).join(", ")}. Remove them to continue.
          </div>
        )}
        {cutoffKitchens.length > 0 && problems.length === 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800 flex gap-3">
            <Clock className="shrink-0 mt-0.5" size={18} />
            <div>
              <p className="font-bold">Past cut-off, asking the chef</p>
              <p className="mt-1">
                {cutoffKitchens.map((k) => `${k.kitchenName}: ${k.items.map((i) => `${i.name} (cut-off ${i.cutoff})`).join(", ")}`).join(" · ")}.
                {" "}We&apos;ll place the order and ask the chef if they can still make it. They have {quote?.confirmMinutes || 15} minutes to reply; if they can&apos;t, that kitchen&apos;s part is cancelled{isWalletSufficient ? " and refunded to your wallet" : ""}. Other kitchens go ahead as normal.
              </p>
            </div>
          </div>
        )}

        <Card className="p-6">
          <h3 className="font-bold text-gray-800 mb-4 border-b pb-2">Payment Method</h3>

          {isWalletSufficient ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-5">
              <div className="flex items-center gap-3 mb-2">
                <Wallet className="text-green-700" />
                <h4 className="font-bold text-green-800">Paying from Wallet</h4>
              </div>
              <p className="text-sm text-green-700">
                Available: <strong>₹{walletBalance.toFixed(2)}</strong>
              </p>
              <p className="text-xs text-green-600 mt-1">
                Remaining: ₹{(walletBalance - totalWithDelivery).toFixed(2)}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-red-500 shrink-0 mt-1" size={20} />
                  <div className="flex-1">
                    <p className="text-sm font-bold text-red-800">Low Wallet Balance</p>
                    <p className="text-xs text-red-600 mt-1">
                      Available: ₹{walletBalance.toFixed(2)}. You need ₹{deficit.toFixed(2)} more.
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <Button
                        size="sm"
                        onClick={openPayUForOrder}
                        disabled={!feesReady || !walletLoaded}
                        className="bg-indigo-600 text-white hover:bg-indigo-700 border-none"
                      >
                        <CreditCard size={14} className="inline mr-1" /> Pay ₹{shortfall.toFixed(2)} with PayU
                      </Button>
                    </div>
                    <p className="text-[10px] text-gray-500 mt-2">
                      PayU charges only the ₹{shortfall.toFixed(2)} shortfall and adds it to your wallet;
                      your wallet then pays the full ₹{totalWithDelivery.toFixed(2)} and your multi-kitchen
                      order is placed automatically when you return.
                      {cutoffKitchens.length > 0 && " If the chef declines a past-cut-off item, its amount goes back to your wallet. To avoid paying before the chef replies, choose cash on delivery."}
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-xs font-bold text-gray-400 uppercase text-center my-2">- OR PAY VIA -</p>

              <label
                className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer ${
                  selectedFallbackMethod === "upi" ? "border-blue-500 bg-blue-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="payment"
                    value="upi"
                    checked={selectedFallbackMethod === "upi"}
                    onChange={() => setSelectedFallbackMethod("upi")}
                    className="accent-blue-600 w-5 h-5"
                  />
                  <div>
                    <div className="font-bold text-gray-900">Pay via UPI</div>
                    <div className="text-xs text-gray-500">Scan QR Code</div>
                  </div>
                </div>
                <span className="text-xl">📱</span>
              </label>

              <label
                className={`flex items-center justify-between p-4 rounded-xl border cursor-pointer ${
                  selectedFallbackMethod === "food" ? "border-green-500 bg-green-50" : "border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <input
                    type="radio"
                    name="payment"
                    value="food"
                    checked={selectedFallbackMethod === "food"}
                    onChange={() => setSelectedFallbackMethod("food")}
                    className="accent-green-600 w-5 h-5"
                  />
                  <div>
                    <div className="font-bold text-gray-900">FOOD (Cash)</div>
                    <div className="text-xs text-gray-500">Pay on Delivery</div>
                  </div>
                </div>
                <span className="text-xl">💰</span>
              </label>
            </div>
          )}
        </Card>

        <div className="fixed bottom-0 left-0 right-0 bg-white border-t p-4 shadow-lg z-50">
          <div className="max-w-2xl mx-auto">
            <Button
              onClick={handleCheckoutClick}
              isLoading={loading || feeLoading}
              disabled={!feesReady}
              className="w-full text-lg py-3"
            >
              {isWalletSufficient
                ? `Pay ₹${totalWithDelivery.toFixed(2)}`
                : selectedFallbackMethod === "upi"
                  ? "Show QR Code"
                  : "Place Order (COD)"}
            </Button>
          </div>
        </div>

        <UPIQRModal
          isOpen={showUPIModal}
          onClose={() => setShowUPIModal(false)}
          onConfirm={() => handlePlaceOrder(true)}
          upiId={merchantVpa}
          amount={totalWithDelivery}
          orderId={orderId}
          merchantName={merchantName}
        />

        {showAddMoney && (
          <AddMoneyModal
            onClose={() => setShowAddMoney(false)}
            initialAmount={addMoneyAmount}
            lockedAmount={addMoneyPurpose === "order_payment"}
            purpose={addMoneyPurpose}
            returnTo="checkout"
            checkoutKey={getCheckoutKey()}
          />
        )}
      </div>
    </div>
  );
};

export default Checkout;
